import { useState, useCallback, useEffect, useRef } from 'react';
import type { FieldRendererProps, FieldDisplayProps } from '../../config/types';
import { Combobox } from '@/components/ui/Combobox';
import { Text } from '@/components/ui/Text';
import { useContextStore } from '@/lib/session/context-store';
import { useAssignableOrgs } from '@/lib/onboarding/use-assignable-orgs';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LEN = 2;

/**
 * Custom field — "Engaging organisation" selector for worker onboarding (ENT-269).
 *
 * Persona behaviour:
 *   CLIENT  → renders nothing (self, no picker needed)
 *   AGENCY  → combobox: "Your organisation (self)" prepended, then managed clients
 *   SA      → combobox: all overseen agencies + clients grouped by persona; required
 *
 * Value: string (the chosen org's tenantId), or undefined (→ self / omit servingTenant).
 */
export function OnboardUnderOrgField({ value, onChange, error }: FieldRendererProps) {
  const active = useContextStore((s) => s.active);
  const memberships = useContextStore((s) => s.memberships);
  const persona = active?.persona;

  const isSA = persona === 'SA';
  const isAgency = persona === 'AGENCY';

  const [searchQ, setSearchQ] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = useCallback((q: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setSearchQ(q), DEBOUNCE_MS);
  }, []);

  /*
    AR-710 — cancel the pending debounce on unmount.

    `handleSearch` clears the timer only when a NEXT search starts, so the last one always outlived
    the component: it fired into an unmounted tree and called `setSearchQ`. In the browser that is a
    no-op React warns about; under Vitest the environment is torn down first, so the timer reached
    `resolveUpdatePriority` after jsdom had removed `window` and vitest failed the whole run with an
    unhandled `ReferenceError: window is not defined` — 3443 tests passing and exit code 1.

    It is a race, so it only bites when teardown wins, which is why it surfaced on CI's slower runner
    and not locally.
  */
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const activeQ = searchQ.length >= MIN_SEARCH_LEN ? searchQ : undefined;
  const orgs = useAssignableOrgs(activeQ, (isSA || isAgency) && Boolean(active));

  if (!isSA && !isAgency) return null;

  const selected = (value as string | undefined) ?? '';

  const managedOptions = (orgs.data ?? []).map((o) => ({
    value: o.tenantId,
    label: o.displayName,
    group: o.persona === 'AGENCY' ? 'Agencies' : 'Managed clients',
    skipFilter: true as const,
  }));

  let options = managedOptions;

  if (isAgency && active) {
    const selfName =
      memberships.find((m) => m.contextTenantId === active.tenantId)?.displayName ??
      'Your organisation';
    const selfOption = {
      value: active.tenantId,
      label: `${selfName} (self)`,
      group: 'Your organisation',
      skipFilter: true as const,
    };
    options = [selfOption, ...managedOptions];
  }

  const grouped = options.some((o) => o.group);
  const placeholder = orgs.isLoading
    ? 'Loading…'
    : isSA
      ? 'Select an organisation…'
      : 'Select a client or your organisation…';

  return (
    <div className="space-y-1">
      <Combobox
        options={options}
        value={selected || (isAgency && active ? active.tenantId : '')}
        onValueChange={(v) => onChange(v || undefined)}
        placeholder={placeholder}
        searchPlaceholder="Search organisations…"
        emptyMessage={
          orgs.isLoading ? 'Loading…' : 'No organisations found.'
        }
        grouped={grouped}
        className="bg-surface-elevated"
        onSearch={handleSearch}
      />
      {orgs.isError && (
        <Text variant="label-sm" color="danger">
          Couldn't load organisations.
        </Text>
      )}
      {!orgs.isLoading && !orgs.isError && options.length === 0 && (
        <Text variant="label-sm" color="subtle">
          No organisations available.
        </Text>
      )}
      {error && (
        <Text variant="label-sm" color="danger">
          {error}
        </Text>
      )}
    </div>
  );
}

/** Review-step display for the engaging org selection. */
export function OnboardUnderOrgDisplay({ value }: FieldDisplayProps) {
  const active = useContextStore((s) => s.active);
  const memberships = useContextStore((s) => s.memberships);
  // Fetch unfiltered list — will hit TanStack Query cache from the edit step.
  const orgs = useAssignableOrgs(undefined, Boolean(active));
  const selected = value as string | undefined;

  if (!selected || (active && selected === active.tenantId)) {
    const selfName =
      memberships.find((m) => m.contextTenantId === active?.tenantId)?.displayName ??
      'Your organisation';
    return (
      <Text variant="body-sm" as="span">
        {selfName} (self)
      </Text>
    );
  }

  if (orgs.isLoading) {
    return <Text variant="body-sm" as="span" color="muted">Loading…</Text>;
  }

  const org = orgs.data?.find((o) => o.tenantId === selected);
  const displayName = org?.displayName ?? selected;
  const personaLabel =
    org?.persona === 'AGENCY' ? 'Agency' : org?.persona === 'CLIENT' ? 'Client' : null;

  return (
    <Text variant="body-sm" as="span">
      {personaLabel ? `${displayName} (${personaLabel})` : displayName}
    </Text>
  );
}
